import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import Table from '../aoraki/common/table'
import { useGetTableData } from '../../hooks/use-api'
import useUser from '../../hooks/use-user'
import ConfirmDelete, { ConfirmDeleteProps } from '../aoraki/forms/confirm-delete'
import {
  ColumnHeader,
  HeadingRow,
  InlineNotification,
  TableAction,
  TableActionType,
  TableSortByProps,
} from '@msaf/core-react'

export interface SummaryTableProps {
  columnHeaders: Array<ColumnHeader>
  fields: Record<string, string | number | boolean>[] | null
  requestUrl: string
  setFields: Dispatch<SetStateAction<Record<string, string | number | boolean>[] | null>>
  defaultSortBy: TableSortByProps
  rowClickAction?: Omit<TableAction, 'handler' | 'actionPermission'>
  tableActionHandlers?: {
    [key in TableActionType]?: (...args: any[]) => void
  }
  showResultsCount?: boolean
  resultNameSingular?: string
  resultNamePlural?: string
  deleteRequestUrl?: string
  confirmDeleteModalProps?: ConfirmDeleteProps
  noResultsComponent?: JSX.Element
  forceRefresh?: boolean
}

export default function SummaryTable({
  columnHeaders,
  fields = [],
  requestUrl,
  setFields,
  defaultSortBy,
  rowClickAction,
  tableActionHandlers,
  showResultsCount,
  resultNameSingular,
  resultNamePlural,
  confirmDeleteModalProps = {
    isShowModal: false,
    onModalClose: () => {},
  },
  noResultsComponent: NoResultsComponent,
  forceRefresh = false,
}: SummaryTableProps) {
  const { user } = useUser()
  const [ordering, setOrdering] = useState<string>(
    `${defaultSortBy.orderDirection === 'desc' ? '-' : ''}${defaultSortBy.orderColumn}`,
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [totalResults, setTotalResults] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [isRefreshTableData, setIsRefreshTableData] = useState(false)
  const [isShowModal, setIsShowModal] = useState(confirmDeleteModalProps?.isShowModal ?? false)
  const fetchUrl = `${requestUrl}${requestUrl.endsWith('/') ? '' : '/'}?ordering=${ordering}&page=${currentPage}`
  const { isSuccess, isLoading, data, refetch } = useGetTableData(fetchUrl, ordering, currentPage)

  useEffect(() => {
    if (confirmDeleteModalProps?.isShowModal) {
      setIsShowModal(confirmDeleteModalProps?.isShowModal)
    }
  }, [confirmDeleteModalProps?.isShowModal])

  useEffect(() => {
    if (isSuccess && data?.results) {
      const { results, currentPage, totalPages, totalResults } = data
      setFields(results)
      setCurrentPage(currentPage)
      setTotalPages(totalPages)
      setTotalResults(totalResults)
      setIsRefreshTableData(false)
    }
  }, [isSuccess, data, setFields])

  useEffect(() => {
    if (forceRefresh) setIsRefreshTableData(true)
  }, [forceRefresh])

  useEffect(() => {
    if (isRefreshTableData) refetch()
  }, [isRefreshTableData, refetch])

  const setPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const setSortColumn = (orderColumn: string, orderDirection: 'desc' | 'asc') => {
    // Updates the `ordering` state. This, in turn, updates the `fetchUrl`.
    // Since, both the url and ordering values are passed as dependency to the query, react-query refetches the data.
    setOrdering(`${orderDirection === 'desc' ? '-' : ''}${orderColumn}`)
  }

  const postDeleteAction = () => {
    setIsRefreshTableData(true)
    if (confirmDeleteModalProps && confirmDeleteModalProps.postDeleteAction) {
      confirmDeleteModalProps.postDeleteAction()
    }
    onModalClose()
  }

  const onModalClose = () => {
    setIsShowModal(false)
    if (confirmDeleteModalProps && confirmDeleteModalProps.onModalClose) {
      confirmDeleteModalProps.onModalClose()
    }
  }

  if (isLoading || fields === null) {
    return <HeadingRow headingLevel={1} headingText='' isSkeleton={isLoading} />
  }

  if (fields && !fields.length) {
    return NoResultsComponent ? (
      NoResultsComponent
    ) : (
      <InlineNotification messageType='info' isDismissable={false}>
        No records have been added. Click 'Add new' to start adding records.
      </InlineNotification>
    )
  }

  return (
    <>
      <ConfirmDelete
        {...confirmDeleteModalProps}
        isShowModal={isShowModal}
        onModalClose={onModalClose}
        postDeleteAction={postDeleteAction}
      >
        <Table
          columnHeaders={columnHeaders}
          tableData={fields || []}
          onRowClick={rowClickAction}
          defaultSortBy={defaultSortBy}
          customActions={tableActionHandlers}
          setSortColumn={setSortColumn}
          currentUserPermissions={user?.permissions}
          currentPage={currentPage}
          setPage={setPage}
          totalResults={totalResults}
          totalPages={totalPages}
          resultNameSingular={resultNameSingular}
          resultNamePlural={resultNamePlural}
          showResultsCount={showResultsCount}
        />
      </ConfirmDelete>
    </>
  )
}
